'use client';

import Link from 'next/link';
import { memo, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { RiskMomentCard } from './RiskMomentCard';
import { CompareWithDialog } from '@/components/comparator/CompareWithDialog';
import { FundSubscribeBell } from './FundSubscribeBell';
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Resize,
  Sort,
} from '@syncfusion/ej2-react-grids';
import { getCorporateEventColor, getCorporateEventDisplayLabel } from '@/libs/corporate-event-chart';
import { useAppColors } from '@/styles/colors';
import { buildComparatorPath } from '@/libs/comparador-route';
import { reportPathForTicker, shouldRenderReportActions } from './ticker-summary-report-actions';
type SeriesPoint = {
  date: string;
  value: number;
};

type Fundamentals = {
  currentPrice: number | null;
  lastPriceDate: string | null;
  currentVpPerShare: number | null;
  pVp: number | null;
  patrimonioLiquido: number | null;
  cotasEmitidas: number | null;
  numeroCotistas: number | null;
  volatilityAnnualized21dPct: number | null;
  distanceTo12mHighPct: number | null;
  selicSensitivityScore: number | null;
  leverageIndexScore: number | null;
  selicSensitivityLabel: string | null;
  leverageIndexLabel: string | null;
  dividendos12m: number | null;
  dy12mPct: number | null;
  dy12mFromDate: string | null;
  dy12mAsOfDate: string | null;
  ultimoDividendo: number | null;
};

type TickerSummaryTabClientProps = {
  tickerLabel: string;
  isLoggedIn: boolean;
  existingPreference: {
    id: number;
    enabled: boolean;
    frequencyDays: number | null;
  } | null;
  fundProfile: {
    ticker: string;
    name: string;
    classification: string | null;
    subClassification: string | null;
    manager: string | null;
  };
  priceSeries: Array<{ date: string; close: number }>;
  vpSeries: Array<{ date: string; vpPorCota: number }>;
  dySeries: Array<{ date: string; dyPercent: number }>;
  pvpSeries: Array<{ date: string; pVp: number }>;
  liquiditySeries: Array<{ date: string; cotistas: number }>;
  subscriptionSeries: Array<{ date: string; amountPerShare: number | null }>;
  corporateEvents: Array<{
    date: string;
    eventType: string;
    label: string | null;
    splitMultiplier: number | null;
    subscriptionPercentage: number | null;
  }>;
  benchmarkSeries: Array<{ date: string; value: number }>;
  latestReportSummaries: Array<{
    id: string;
    title: string;
    deliveryDate: string | null;
    resumo: string | null;
    sentimentoGeral: string | null;
    sentimentoDy: string | null;
    sentimentoVacanciaInadimplencia: string | null;
  }>;
  fundamentals: Fundamentals;
  portfolioHighlights: {
    referenceDate: string | null;
    coverageLabel: string;
    topPositions: Array<{ name: string; portfolioPercent: number | null }>;
    mix: {
      realEstatePercent: number;
      fundsPercent: number;
      financialAssetsPercent: number;
      equitiesPercent: number;
    } | null;
    concentration: {
      top1Percent: number | null;
      top5Percent: number | null;
    } | null;
    concentrationByProfile: {
      realEstateTop1Percent: number | null;
      realEstateTop5Percent: number | null;
      paperTop1Percent: number | null;
      paperTop5Percent: number | null;
    } | null;
    riskExposure: {
      ipcaIgpmPercent: number | null;
      maturityUpTo12MonthsPercent: number | null;
    } | null;
    financialPortfolioPercent: number | null;
    realEstateTopStates: Array<{
      state: string;
      percent: number;
      basis: 'weight' | 'count';
    }>;
    cashSummary: {
      cashTotal: number | null;
      receivableTotal: number | null;
      payableTotal: number | null;
      netLiquidity: number | null;
      coverageRatio: number | null;
      cashOverInvestedPercent: number | null;
      netLiquidityDelta: number | null;
    } | null;
    realEstateSummary: {
      totalAssets: number | null;
      vacancyAvgPercent: number | null;
      delinquencyAvgPercent: number | null;
      assetsWithVacancy: number | null;
      assetsWithDelinquency: number | null;
      typicalContractsPercent: number | null;
      atypicalContractsPercent: number | null;
    } | null;
    paperSummary: {
      financialAssetsPercent: number | null;
      fundsPercent: number | null;
      top5ConcentrationPercent: number | null;
      inflationExposurePercent: number | null;
      maturityUpTo12MonthsPercent: number | null;
      dominantIndexerLabel: string | null;
      dominantIndexerPercent: number | null;
    } | null;
  } | null;
  quickSimulationSnapshots: Array<{
    id: '6M' | '1A' | '2A';
    label: string;
    actualStartDate: string;
    actualEndDate: string;
    initialAmount: number;
    finalPriceOnly: number;
    finalReinvested: number;
    returnPriceOnlyPct: number;
    returnReinvestedPct: number;
  }>;
  relatedComparisons: Array<{
    ticker: string;
    name: string | null;
    rankOrder: number;
    similarityScore: number;
  }>;
  g2rsMode?: boolean;
};

type RangeId = '6M' | '1A' | '3A' | '5A' | 'TUDO';

const DAY_MS = 24 * 60 * 60 * 1000;
const PLOT_VERTICAL_PADDING_RATIO = 0.08;

const RANGE_CONFIG: Array<{ id: RangeId; label: string; days: number | null }> = [
  { id: '6M', label: '6M', days: 180 },
  { id: '1A', label: '1A', days: 365 },
  { id: '3A', label: '3A', days: 365 * 3 },
  { id: '5A', label: '5A', days: 365 * 5 },
  { id: 'TUDO', label: 'TUDO', days: null },
];

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});

const RATIO_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

const toTs = (date: string) => new Date(`${date}T00:00:00Z`).getTime();
const formatIsoDate = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '-';
  return DATE_FORMATTER.format(date);
};

const normalizeInstrumentForCompare = (value: string): string | null => {
  const normalized = value
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]/g, '');
  if (!normalized) return null;
  if (/^[A-Z]{4}$/.test(normalized)) return `${normalized}11`;
  return normalized;
};

const buildComparadorPathFromAssets = (items: string[]): string => {
  return buildComparatorPath(items);
};

export function TickerSummaryTabClient({
  tickerLabel,
  isLoggedIn,
  existingPreference,
  fundProfile,
  priceSeries,
  vpSeries,
  dySeries,
  pvpSeries,
  liquiditySeries,
  subscriptionSeries: _subscriptionSeries,
  corporateEvents,
  benchmarkSeries,
  latestReportSummaries,
  fundamentals,
  portfolioHighlights,
  quickSimulationSnapshots,
  relatedComparisons,
  g2rsMode = false,
}: TickerSummaryTabClientProps) {
  const theme = useTheme();
  const colors = useAppColors();
  const isLight = theme.palette.mode === 'light';
  const historyColors = colors.charts.summaryHistory;
  const rangeColors = colors.actions.range;
  const accentOutline = colors.actions.accentOutline;
  const compareButtonColors = colors.actions.compareButton;
  const [range, setRange] = useState<RangeId>('1A');
  const [hoveredDateIndex, setHoveredDateIndex] = useState<number | null>(null);
  const [showPrice, setShowPrice] = useState(true);
  const [showVp, setShowVp] = useState(false);
  const [showDy, setShowDy] = useState(false);
  const [showPvp, setShowPvp] = useState(false);
  const [showBenchmark, setShowBenchmark] = useState(true);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [compareSelection, setCompareSelection] = useState<string[]>([]);

  const currentInstrumentLabel = useMemo(
    () => normalizeInstrumentForCompare(fundProfile.ticker) ?? fundProfile.ticker.toUpperCase(),
    [fundProfile.ticker],
  );
  const quickCompareLinks = useMemo(
    () =>
      relatedComparisons.map((item) => ({
        label: item.ticker,
        href: buildComparadorPathFromAssets([currentInstrumentLabel, item.ticker]),
        tooltip: item.name ? `${item.ticker} · ${item.name}` : item.ticker,
      })),
    [currentInstrumentLabel, relatedComparisons],
  );
  const top4RelatedHref = useMemo(() => {
    const top4 = relatedComparisons.slice(0, 4).map((item) => item.ticker);
    if (top4.length < 4) return null;
    return buildComparadorPathFromAssets([currentInstrumentLabel, ...top4]);
  }, [currentInstrumentLabel, relatedComparisons]);

  useEffect(() => {
    setCompareSelection((previous) => {
      if (previous.length > 0) return previous;
      return [currentInstrumentLabel];
    });
  }, [currentInstrumentLabel]);
  const normalizedPrice = useMemo<SeriesPoint[]>(
    () =>
      [...priceSeries]
        .map((item) => ({ date: item.date, value: item.close }))
        .sort((a, b) => toTs(a.date) - toTs(b.date)),
    [priceSeries],
  );

  const normalizedVp = useMemo<SeriesPoint[]>(
    () =>
      [...vpSeries]
        .map((item) => ({ date: item.date, value: item.vpPorCota }))
        .sort((a, b) => toTs(a.date) - toTs(b.date)),
    [vpSeries],
  );
  const normalizedDy = useMemo<SeriesPoint[]>(
    () =>
      [...dySeries]
        .map((item) => ({ date: item.date, value: item.dyPercent }))
        .sort((a, b) => toTs(a.date) - toTs(b.date)),
    [dySeries],
  );
  const normalizedPvp = useMemo<SeriesPoint[]>(
    () =>
      [...pvpSeries].map((item) => ({ date: item.date, value: item.pVp })).sort((a, b) => toTs(a.date) - toTs(b.date)),
    [pvpSeries],
  );
  const normalizedLiquidity = useMemo<SeriesPoint[]>(
    () =>
      [...liquiditySeries]
        .map((item) => ({ date: item.date, value: item.cotistas }))
        .sort((a, b) => toTs(a.date) - toTs(b.date)),
    [liquiditySeries],
  );
  const normalizedBenchmark = useMemo<SeriesPoint[]>(
    () =>
      [...benchmarkSeries]
        .map((item) => ({ date: item.date, value: item.value }))
        .sort((a, b) => toTs(a.date) - toTs(b.date)),
    [benchmarkSeries],
  );

  const latestTs = useMemo(() => {
    const dates = [
      normalizedPrice.length > 0 ? toTs(normalizedPrice[normalizedPrice.length - 1].date) : null,
      normalizedVp.length > 0 ? toTs(normalizedVp[normalizedVp.length - 1].date) : null,
      normalizedDy.length > 0 ? toTs(normalizedDy[normalizedDy.length - 1].date) : null,
      normalizedPvp.length > 0 ? toTs(normalizedPvp[normalizedPvp.length - 1].date) : null,
      normalizedLiquidity.length > 0 ? toTs(normalizedLiquidity[normalizedLiquidity.length - 1].date) : null,
      normalizedBenchmark.length > 0 ? toTs(normalizedBenchmark[normalizedBenchmark.length - 1].date) : null,
    ].filter((value): value is number => value !== null);
    return dates.length > 0 ? Math.max(...dates) : null;
  }, [normalizedPrice, normalizedVp, normalizedDy, normalizedPvp, normalizedLiquidity, normalizedBenchmark]);

  const filtered = useMemo(() => {
    if (latestTs === null) {
      return {
        price: normalizedPrice,
        vp: normalizedVp,
        dy: normalizedDy,
        pvp: normalizedPvp,
        benchmark: normalizedBenchmark,
      };
    }
    const selected = RANGE_CONFIG.find((item) => item.id === range);
    if (!selected || selected.days === null) {
      return {
        price: normalizedPrice,
        vp: normalizedVp,
        dy: normalizedDy,
        pvp: normalizedPvp,
        benchmark: normalizedBenchmark,
      };
    }
    const threshold = latestTs - selected.days * DAY_MS;
    const price = normalizedPrice.filter((item) => toTs(item.date) >= threshold);
    const vp = normalizedVp.filter((item) => toTs(item.date) >= threshold);
    const dy = normalizedDy.filter((item) => toTs(item.date) >= threshold);
    const pvp = normalizedPvp.filter((item) => toTs(item.date) >= threshold);
    const benchmark = normalizedBenchmark.filter((item) => toTs(item.date) >= threshold);
    return {
      price: price.length > 0 ? price : normalizedPrice,
      vp: vp.length > 0 ? vp : normalizedVp,
      dy: dy.length > 0 ? dy : normalizedDy,
      pvp: pvp.length > 0 ? pvp : normalizedPvp,
      benchmark: benchmark.length > 0 ? benchmark : normalizedBenchmark,
    };
  }, [latestTs, normalizedPrice, normalizedVp, normalizedDy, normalizedPvp, normalizedBenchmark, range]);
  const hasBenchmark = filtered.benchmark.length > 0;

  const chartData = useMemo(() => {
    let normalizedBenchmarkForChart: SeriesPoint[] = [];
    if (filtered.benchmark.length > 0 && filtered.price.length > 0 && filtered.benchmark[0].value > 0) {
      const factor = filtered.price[0].value / filtered.benchmark[0].value;
      normalizedBenchmarkForChart = filtered.benchmark.map((item) => ({
        date: item.date,
        value: item.value * factor,
      }));
    }
    const allDates = Array.from(
      new Set([
        ...filtered.price.map((item) => item.date),
        ...filtered.vp.map((item) => item.date),
        ...filtered.dy.map((item) => item.date),
        ...filtered.pvp.map((item) => item.date),
        ...normalizedBenchmarkForChart.map((item) => item.date),
      ]),
    ).sort((a, b) => toTs(a) - toTs(b));

    const priceMap = new Map(filtered.price.map((item) => [item.date, item.value]));
    const vpMap = new Map(filtered.vp.map((item) => [item.date, item.value]));
    const dyMap = new Map(filtered.dy.map((item) => [item.date, item.value]));
    const pvpMap = new Map(filtered.pvp.map((item) => [item.date, item.value]));
    const benchmarkMap = new Map(filtered.benchmark.map((item) => [item.date, item.value]));
    const normalizedBenchmarkMap = new Map(normalizedBenchmarkForChart.map((item) => [item.date, item.value]));
    const normalizedBenchmarkCarryMap = new Map<string, number | null>();
    const vpCarryMap = new Map<string, number | null>();
    const dyCarryMap = new Map<string, number | null>();
    const benchmarkCarryMap = new Map<string, number | null>();
    const values = [
      ...filtered.price.map((item) => item.value),
      ...filtered.vp.map((item) => item.value),
      ...normalizedBenchmarkForChart.map((item) => item.value),
    ];
    if (allDates.length === 0 || values.length === 0) {
      return null;
    }
    const dyValues = filtered.dy.map((item) => item.value);
    const pvpValues = filtered.pvp.map((item) => item.value);

    const minData = Math.min(...values);
    const maxData = Math.max(...values);
    const rangeData = maxData - minData;
    const yPadding = (rangeData === 0 ? Math.max(Math.abs(maxData), 1) : rangeData) * 0.08;
    const min = minData - yPadding;
    const max = maxData + yPadding;
    const startTs = toTs(allDates[0]);
    const endTs = toTs(allDates[allDates.length - 1]);

    let lastVpValue: number | null = null;
    let lastDyValue: number | null = null;
    let lastBenchmarkValue: number | null = null;
    let lastNormalizedBenchmarkValue: number | null = null;
    for (const date of allDates) {
      if (vpMap.has(date)) {
        lastVpValue = vpMap.get(date) ?? null;
      }
      if (dyMap.has(date)) {
        lastDyValue = dyMap.get(date) ?? null;
      }
      if (benchmarkMap.has(date)) {
        lastBenchmarkValue = benchmarkMap.get(date) ?? null;
      }
      if (normalizedBenchmarkMap.has(date)) {
        lastNormalizedBenchmarkValue = normalizedBenchmarkMap.get(date) ?? null;
      }
      vpCarryMap.set(date, lastVpValue);
      dyCarryMap.set(date, lastDyValue);
      benchmarkCarryMap.set(date, lastBenchmarkValue);
      normalizedBenchmarkCarryMap.set(date, lastNormalizedBenchmarkValue);
    }

    const dyMinData = dyValues.length > 0 ? Math.min(...dyValues, 0) : 0;
    const dyMaxData = dyValues.length > 0 ? Math.max(...dyValues, 0) : 0;
    const dyRange = dyMaxData - dyMinData;
    const dyPadding = (dyRange === 0 ? Math.max(Math.abs(dyMaxData), 0.1) : dyRange) * 0.08;
    const dyMin = dyMinData - dyPadding;
    const dyMax = dyMaxData + dyPadding;

    const pvpMinData = pvpValues.length > 0 ? Math.min(...pvpValues, 0) : 0;
    const pvpMaxData = pvpValues.length > 0 ? Math.max(...pvpValues, 0) : 0;
    const pvpRange = pvpMaxData - pvpMinData;
    const pvpPadding = (pvpRange === 0 ? Math.max(Math.abs(pvpMaxData), 0.1) : pvpRange) * 0.08;
    const pvpMin = pvpMinData - pvpPadding;
    const pvpMax = pvpMaxData + pvpPadding;

    return {
      allDates,
      priceMap,
      vpMap,
      dyMap,
      pvpMap,
      benchmarkMap,
      normalizedBenchmarkMap,
      normalizedBenchmarkCarryMap,
      vpCarryMap,
      dyCarryMap,
      benchmarkCarryMap,
      min,
      max,
      dyMin,
      dyMax,
      pvpMin,
      pvpMax,
      startTs,
      endTs,
    };
  }, [filtered.price, filtered.vp, filtered.dy, filtered.pvp, filtered.benchmark]);
  const filteredCorporateEvents = useMemo(() => {
    if (!chartData) return [];
    const startDate = chartData.allDates[0];
    const endDate = chartData.allDates[chartData.allDates.length - 1];
    return corporateEvents
      .filter((event) => event.date >= startDate && event.date <= endDate)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [chartData, corporateEvents]);

  const fundamentalsRows = useMemo(() => {
    const marketCap =
      fundamentals.currentPrice !== null && fundamentals.cotasEmitidas !== null
        ? fundamentals.currentPrice * fundamentals.cotasEmitidas
        : null;
    const dy12m = fundamentals.dy12mPct;
    return [
      {
        title: 'PREÇO ATUAL',
        main: formatCurrency(fundamentals.currentPrice),
        secondaryLabel: 'ÚLTIMO DIVIDENDO',
        secondaryValue: formatCurrency(fundamentals.ultimoDividendo),
        titleTooltip:
          fundamentals.lastPriceDate === null
            ? 'Preço ajustado da última negociação, sem data disponível.'
            : `Preço ajustado da última negociação ocorrida em ${formatIsoDate(fundamentals.lastPriceDate)}.`,
      },
      {
        title: 'VAL. PATRIMONIAL P/COTA',
        main: formatCurrency(fundamentals.currentVpPerShare),
        secondaryLabel: 'PATRIMÔNIO',
        secondaryValue: formatCurrency(fundamentals.patrimonioLiquido),
      },
      {
        title: 'P/VP',
        main: fundamentals.pVp === null ? '-' : RATIO_FORMATTER.format(fundamentals.pVp),
        secondaryLabel: 'VALOR DE MERCADO',
        secondaryValue: formatCurrency(marketCap),
      },
      {
        title: 'DY (12M)',
        main: dy12m === null ? '-' : `${PERCENT_FORMATTER.format(dy12m)}%`,
        secondaryLabel: 'DIVIDENDOS (12M)',
        secondaryValue: formatCurrency(fundamentals.dividendos12m),
        titleTooltip:
          fundamentals.dy12mFromDate && fundamentals.dy12mAsOfDate
            ? `Janela de cálculo do DY 12M: ${formatIsoDate(fundamentals.dy12mFromDate)} a ${formatIsoDate(fundamentals.dy12mAsOfDate)}.`
            : 'Janela de cálculo do DY 12M indisponível.',
      },
      {
        title: 'Nº DE COTISTAS',
        main: formatNumber(fundamentals.numeroCotistas),
        secondaryLabel: 'Nº DE COTAS',
        secondaryValue: formatNumber(fundamentals.cotasEmitidas),
      },
    ];
  }, [fundamentals]);

  const quickRead = useMemo(() => {
    const classification = fundProfile.classification ?? 'ativo';
    const availableSignals = [
      fundamentals.currentPrice,
      fundamentals.pVp,
      fundamentals.dy12mPct,
      fundamentals.patrimonioLiquido,
    ].filter((value) => value !== null).length;

    if (availableSignals < 2) {
      return `${fundProfile.ticker} é um ${classification} com histórico ainda limitado nesta base. Os dados disponíveis aparecem abaixo, e novas informações serão incorporadas conforme os relatórios forem publicados.`;
    }

    const headlineSignals = [
      fundamentals.currentPrice !== null ? 'cotação atual' : null,
      fundamentals.dy12mPct !== null ? 'dividendos' : null,
      fundamentals.pVp !== null ? 'relação P/VP' : null,
      fundamentals.patrimonioLiquido !== null ? 'patrimônio' : null,
    ].filter((value): value is string => value !== null);

    return `${fundProfile.ticker} é um ${classification} com dados de ${headlineSignals.join(', ')}. Use o histórico e os indicadores para entender o comportamento do fundo antes de comparar com alternativas.`;
  }, [
    fundProfile.classification,
    fundProfile.ticker,
    fundamentals.currentPrice,
    fundamentals.patrimonioLiquido,
    fundamentals.dy12mPct,
    fundamentals.pVp,
  ]);

  const quantitativeIndicators = useMemo(() => {
    const labelColors = colors.indicators.label;
    const distanceColors = colors.indicators.distance;
    const accentColors = colors.indicators.accent;
    const classifyLabelColor = (label: string | null) => {
      const normalized = (label ?? '').trim().toUpperCase();
      if (normalized === 'ALTA') return labelColors.high;
      if (normalized === 'MEDIA') return labelColors.medium;
      if (normalized === 'BAIXA') return labelColors.low;
      return labelColors.muted;
    };

    const classifyDistanceLabel = (value: number | null) => {
      if (value === null) return 'Não informado';
      if (value <= 5) return 'PRÓXIMO DA MÁXIMA';
      if (value <= 20) return 'DISTÂNCIA MODERADA';
      return 'DISTANTE DA MÁXIMA';
    };

    const classifyDistanceLabelColor = (value: number | null) => {
      if (value === null) return distanceColors.muted;
      if (value <= 5) return distanceColors.near;
      if (value <= 20) return distanceColors.medium;
      return distanceColors.far;
    };

    const classifyVacancyLabel = (value: number | null) => {
      if (value === null) return 'Não informado';
      if (value < 5) return 'BAIXA';
      if (value < 12) return 'MEDIA';
      return 'ALTA';
    };

    const vacancyAvgPercent = portfolioHighlights?.realEstateSummary?.vacancyAvgPercent ?? null;
    const delinquencyAvgPercent = portfolioHighlights?.realEstateSummary?.delinquencyAvgPercent ?? null;

    const classifyDelinquencyLabel = (value: number | null) => {
      if (value === null) return 'Não informado';
      if (value < 2) return 'BAIXA';
      if (value < 5) return 'MEDIA';
      return 'ALTA';
    };

    return [
      {
        id: 'selic-sensitivity',
        title: 'Sensibilidade à Selic',
        score: fundamentals.selicSensitivityScore,
        value:
          fundamentals.selicSensitivityScore === null
            ? '-'
            : `${PERCENT_FORMATTER.format(fundamentals.selicSensitivityScore)}%`,
        label: fundamentals.selicSensitivityLabel ?? 'Não informado',
        labelColor: classifyLabelColor(fundamentals.selicSensitivityLabel),
        accentColor: accentColors.selic,
        detail: 'Estimativa de quanto o resultado do fundo tende a reagir a mudanças da taxa de juros.',
      },
      {
        id: 'leverage-index',
        title: 'Índice de alavancagem',
        score: fundamentals.leverageIndexScore,
        value:
          fundamentals.leverageIndexScore === null
            ? '-'
            : `${PERCENT_FORMATTER.format(fundamentals.leverageIndexScore)}%`,
        label: fundamentals.leverageIndexLabel ?? 'Não informado',
        labelColor: classifyLabelColor(fundamentals.leverageIndexLabel),
        accentColor: accentColors.leverage,
        detail: 'Indicador de pressão financeira com base em obrigações e recursos líquidos em relação ao patrimônio.',
      },
      {
        id: 'distance-to-high-12m',
        title: 'Distância da máxima (12m)',
        score: fundamentals.distanceTo12mHighPct,
        value:
          fundamentals.distanceTo12mHighPct === null
            ? '-'
            : `${PERCENT_FORMATTER.format(fundamentals.distanceTo12mHighPct)}%`,
        label: classifyDistanceLabel(fundamentals.distanceTo12mHighPct),
        labelColor: classifyDistanceLabelColor(fundamentals.distanceTo12mHighPct),
        accentColor: accentColors.distance,
        detail:
          'Mostra quanto o preço atual está abaixo da máxima dos últimos 12 meses: ((máxima 12m - preço atual) / máxima 12m) * 100.',
      },
      {
        id: 'vacancy-avg',
        title: 'Vacância média',
        score: vacancyAvgPercent,
        value: vacancyAvgPercent === null ? '-' : `${PERCENT_FORMATTER.format(vacancyAvgPercent)}%`,
        label: classifyVacancyLabel(vacancyAvgPercent),
        labelColor: classifyLabelColor(classifyVacancyLabel(vacancyAvgPercent)),
        accentColor: accentColors.vacancy,
        detail:
          'Média de vacância dos ativos imobiliários informados no último relatório disponível (áreas vazias em relação ao total locável).',
      },
      {
        id: 'delinquency-avg',
        title: 'Inadimplência média',
        score: delinquencyAvgPercent,
        value: delinquencyAvgPercent === null ? '-' : `${PERCENT_FORMATTER.format(delinquencyAvgPercent)}%`,
        label: classifyDelinquencyLabel(delinquencyAvgPercent),
        labelColor: classifyLabelColor(classifyDelinquencyLabel(delinquencyAvgPercent)),
        accentColor: accentColors.delinquency,
        detail: 'Média de inadimplência reportada para os ativos imobiliários no último relatório disponível.',
      },
    ];
  }, [
    fundamentals.distanceTo12mHighPct,
    colors,
    isLight,
    fundamentals.leverageIndexLabel,
    fundamentals.leverageIndexScore,
    portfolioHighlights?.realEstateSummary?.delinquencyAvgPercent,
    portfolioHighlights?.realEstateSummary?.vacancyAvgPercent,
    fundamentals.selicSensitivityLabel,
    fundamentals.selicSensitivityScore,
  ]);

  const width = 1000;
  const height = 360;
  const chartLeft = 72;
  const chartRight = width - 148;
  const chartTop = 24;
  const chartBottom = height - 48;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;

  const mapTsToX = (ts: number, minTs: number, maxTs: number) => {
    if (maxTs === minTs) return chartLeft + chartWidth / 2;
    return chartLeft + ((ts - minTs) / (maxTs - minTs)) * chartWidth;
  };

  const mapValueToY = (value: number, minValue: number, maxValue: number) => {
    const innerPadding = chartHeight * PLOT_VERTICAL_PADDING_RATIO;
    const usableHeight = Math.max(1, chartHeight - innerPadding * 2);
    if (maxValue === minValue) return chartTop + innerPadding + usableHeight / 2;
    const ratio = (value - minValue) / (maxValue - minValue);
    return chartTop + innerPadding + usableHeight - ratio * usableHeight;
  };

  const quantitativeIndicatorsSection = (
    <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
      <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='h6' fontWeight={700}>
          Indicadores Quantitativos
        </Typography>
        <InfoDotTooltip text='Indicadores numéricos calculados para apoiar leitura de sensibilidade a juros e pressão financeira.' />
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gap: 1.1,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(5, minmax(0, 1fr))',
          },
        }}
      >
        {quantitativeIndicators.map((indicator) => (
          <Box
            key={indicator.id}
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid hsl(var(--border))',
              borderTop: `3px solid ${indicator.accentColor}`,
              bgcolor: 'hsl(var(--card))',
            }}
          >
            <Stack direction='row' spacing={0.6} alignItems='center'>
              <Typography variant='subtitle2' fontWeight={700}>
                {indicator.title}
              </Typography>
              <InfoDotTooltip text={indicator.detail} />
            </Stack>
            <Stack direction='row' justifyContent='space-between' alignItems='baseline' sx={{ mt: 0.8 }}>
              <Typography variant='h5' fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {indicator.value}
              </Typography>
              <Chip
                size='small'
                label={indicator.label}
                variant='outlined'
                sx={{
                  height: 22,
                  fontWeight: 700,
                  color: indicator.labelColor,
                  borderColor: alpha(indicator.labelColor, 0.45),
                  backgroundColor: alpha(indicator.labelColor, isLight ? 0.12 : 0.2),
                }}
              />
            </Stack>
            <LinearProgress
              variant='determinate'
              value={Math.max(0, Math.min(100, indicator.score ?? 0))}
              sx={{
                mt: 0.8,
                height: 8,
                borderRadius: 999,
                backgroundColor: 'hsl(var(--muted))',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: indicator.accentColor,
                },
              }}
            />
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.7, display: 'block' }}>
              Escala de 0% a 100%
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            border: '1px solid hsl(var(--border))',
            borderRadius: 1.5,
            bgcolor: 'hsl(var(--muted) / 0.35)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 1, md: 3 }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            flexWrap='wrap'
            useFlexGap
          >
            <Typography variant='h5' fontWeight={800} sx={{ lineHeight: 1.1, mr: { md: 1 } }}>
              {fundProfile.ticker}
            </Typography>
            <Typography
              variant='body1'
              fontWeight={700}
              sx={{
                flexBasis: { xs: '100%', md: 'auto' },
                maxWidth: { xs: '100%', md: 360 },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: { xs: 'normal', md: 'nowrap' },
              }}
              title={fundProfile.name}
            >
              {fundProfile.name}
            </Typography>
            <Chip size='small' label={fundProfile.classification ?? 'Sem classificação'} variant='outlined' />
            {fundProfile.subClassification ? (
              <Chip size='small' label={fundProfile.subClassification} variant='outlined' />
            ) : null}
            <Typography variant='body2' color='text.secondary' sx={{ ml: { md: 1 } }}>
              {fundProfile.manager
                ? `Gestão/administradora: ${fundProfile.manager}`
                : 'Gestão/administradora não informada'}
            </Typography>
            <Button
              size='small'
              variant='contained'
              onClick={() => setIsCompareModalOpen(true)}
              sx={{
                ml: { xs: 'auto', md: 'auto' },
                color: compareButtonColors.text,
                backgroundColor: compareButtonColors.bg,
                '&:hover': {
                  backgroundColor: compareButtonColors.hoverBg,
                },
              }}
            >
              <Box component='span' sx={{ mr: 0.7, lineHeight: 1 }} aria-hidden='true'>
                ⇄
              </Box>
              Comparar com
            </Button>
            {shouldRenderReportActions(g2rsMode) ? (
              <Button
                component={Link}
                href={reportPathForTicker(fundProfile.ticker)}
                size='small'
                variant='outlined'
                sx={{
                  ml: { xs: 'auto', md: 0 },
                  color: 'text.primary',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Relatórios
              </Button>
            ) : null}
          </Stack>
          {quickCompareLinks.length > 0 ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent='space-between'
              sx={{ mt: 1 }}
            >
              <FundSubscribeBell
                ticker={tickerLabel}
                isLoggedIn={isLoggedIn}
                existingPreference={existingPreference}
                g2rsMode={g2rsMode}
              />
              <Box sx={{ maxWidth: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap',
                    gap: 0.8,
                  }}
                >
                  {quickCompareLinks.map((item) => (
                    <Tooltip key={item.label} title={item.tooltip} arrow>
                      <Chip
                        component={Link}
                        href={item.href}
                        clickable
                        size='small'
                        label={
                          <Box
                            component='span'
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.45,
                            }}
                          >
                            <Box component='span' sx={{ fontSize: 12, lineHeight: 1 }} aria-hidden='true'>
                              ⇄
                            </Box>
                            {item.label}
                          </Box>
                        }
                        sx={{
                          color: accentOutline.text,
                          borderColor: accentOutline.border,
                          backgroundColor: 'transparent',
                          flexShrink: 0,
                          '&:hover': {
                            color: accentOutline.hoverText,
                            borderColor: accentOutline.hoverBorder,
                            backgroundColor: accentOutline.hoverBg,
                          },
                        }}
                        variant='outlined'
                      />
                    </Tooltip>
                  ))}
                  {top4RelatedHref ? (
                    <Tooltip title='Comparar com os 4 relacionados mais próximos' arrow>
                      <Chip
                        component={Link}
                        href={top4RelatedHref}
                        clickable
                        size='small'
                        label={
                          <Box
                            component='span'
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.45,
                            }}
                          >
                            <Box component='span' sx={{ fontSize: 12, lineHeight: 1 }} aria-hidden='true'>
                              ⇄
                            </Box>
                            Top 4
                          </Box>
                        }
                        sx={{
                          color: accentOutline.text,
                          borderColor: accentOutline.border,
                          backgroundColor: 'transparent',
                          flexShrink: 0,
                          fontWeight: 700,
                          '&:hover': {
                            color: accentOutline.hoverText,
                            borderColor: accentOutline.hoverBorder,
                            backgroundColor: accentOutline.hoverBg,
                          },
                        }}
                        variant='outlined'
                      />
                    </Tooltip>
                  ) : null}
                </Box>
              </Box>
            </Stack>
          ) : null}
          <Box
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 1.25,
              borderLeft: '3px solid',
              borderColor: 'primary.main',
              backgroundColor: 'hsl(var(--card) / 0.7)',
              borderRadius: '0 12px 12px 0',
            }}
          >
            <Typography
              variant='overline'
              sx={{
                color: 'primary.main',
                fontWeight: 800,
                letterSpacing: 1.1,
              }}
            >
              Leitura rápida
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {quickRead}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' },
            border: '1px solid hsl(var(--border))',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {fundamentalsRows.map((item, index) => (
            <Box
              key={item.title}
              sx={{
                p: 2,
                borderRight: {
                  xs: 'none',
                  md: index < fundamentalsRows.length - 1 ? '1px solid hsl(var(--border))' : 'none',
                },
                borderBottom: {
                  xs: index < fundamentalsRows.length - 1 ? '1px solid hsl(var(--border))' : 'none',
                  md: 'none',
                },
              }}
            >
              <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>
                <Box
                  component='span'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {item.title}
                  {'titleTooltip' in item && item.titleTooltip ? <InfoDotTooltip text={item.titleTooltip} /> : null}
                </Box>
              </Typography>
              <Typography variant='h4' fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1 }}>
                {item.main}
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  mt: 1,
                  display: 'block',
                }}
              >
                {item.secondaryLabel}
              </Typography>
              <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.1 }}>
                {item.secondaryValue}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <CompareWithDialog
        open={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        currentInstrument={currentInstrumentLabel}
        selectedInstruments={compareSelection}
        onSelectedInstrumentsChange={setCompareSelection}
        maxTotal={5}
      />

      {quantitativeIndicatorsSection}

      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Typography variant='h6' fontWeight={700}>
            Histórico do Fundo
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {RANGE_CONFIG.map((item) => (
              <Button
                key={item.id}
                size='small'
                variant={range === item.id ? 'contained' : 'outlined'}
                onClick={() => setRange(item.id)}
                sx={
                  isLight
                    ? range === item.id
                      ? {
                          bgcolor: rangeColors.selectedBg,
                          borderColor: rangeColors.selectedBorder,
                          color: rangeColors.selectedText,
                          '&:hover': {
                            bgcolor: rangeColors.selectedHoverBg,
                            borderColor: rangeColors.selectedHoverBorder,
                          },
                        }
                      : {
                          color: rangeColors.unselectedText,
                          borderColor: rangeColors.unselectedBorder,
                          '&:hover': {
                            color: rangeColors.unselectedHoverText,
                            borderColor: rangeColors.unselectedHoverBorder,
                            backgroundColor: rangeColors.unselectedHoverBg,
                          },
                        }
                    : undefined
                }
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Stack>
        <Stack direction='row' spacing={1} sx={{ mt: 1 }} flexWrap='wrap' useFlexGap>
          <SeriesToggleButton
            label='Cotação'
            color={historyColors.price}
            active={showPrice}
            onClick={() => setShowPrice((value) => !value)}
          />
          <SeriesToggleButton
            label='VP/cota'
            color={historyColors.vp}
            active={showVp}
            onClick={() => setShowVp((value) => !value)}
          />
          <SeriesToggleButton
            label='DY pago no mês (%)'
            color={historyColors.dy}
            active={showDy}
            onClick={() => setShowDy((value) => !value)}
          />
          <SeriesToggleButton
            label='P/VP histórico'
            color={historyColors.pvp}
            active={showPvp}
            onClick={() => setShowPvp((value) => !value)}
          />
          {hasBenchmark ? (
            <SeriesToggleButton
              label='IFIX (base)'
              color={historyColors.benchmark}
              active={showBenchmark}
              onClick={() => setShowBenchmark((value) => !value)}
            />
          ) : null}
        </Stack>

        {!chartData ? (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Sem dados suficientes para o gráfico.
          </Typography>
        ) : (
          <Box sx={{ width: '100%', mt: 1.5 }}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              width='100%'
              role='img'
              onMouseLeave={() => setHoveredDateIndex(null)}
              onMouseMove={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                if (rect.width <= 0 || chartWidth <= 0) return;
                const relativeX = ((event.clientX - rect.left) / rect.width) * width;
                const clampedX = Math.max(chartLeft, Math.min(chartRight, relativeX));
                const ratio = (clampedX - chartLeft) / chartWidth;
                const index = Math.round(ratio * (chartData.allDates.length - 1));
                const safeIndex = Math.max(0, Math.min(chartData.allDates.length - 1, index));
                setHoveredDateIndex(safeIndex);
              }}
            >
              <title>{`Gráfico de cotação e VP por cota de ${tickerLabel}`}</title>
              <rect
                x={chartLeft}
                y={chartTop}
                width={chartWidth}
                height={chartHeight}
                fill='transparent'
                stroke='hsl(var(--border))'
              />

              {(showPrice || showVp) &&
                Array.from({ length: 5 }).map((_, idx) => {
                  const ratio = idx / 4;
                  const y = chartTop + ratio * chartHeight;
                  const value = chartData.max - ratio * (chartData.max - chartData.min);
                  return (
                    <g key={idx}>
                      <line
                        x1={chartLeft}
                        y1={y}
                        x2={chartRight}
                        y2={y}
                        stroke='hsl(var(--border))'
                        strokeDasharray='4 4'
                      />
                      <text
                        x={chartLeft - 8}
                        y={y + 4}
                        textAnchor='end'
                        fontSize='12'
                        fill='hsl(var(--muted-foreground))'
                      >
                        {BRL_FORMATTER.format(value)}
                      </text>
                    </g>
                  );
                })}

              {showPrice ? (
                <polyline
                  points={filtered.price
                    .map((item) => {
                      const x = mapTsToX(toTs(item.date), chartData.startTs, chartData.endTs);
                      const y = mapValueToY(item.value, chartData.min, chartData.max);
                      return `${x},${y}`;
                    })
                    .join(' ')}
                  fill='none'
                  stroke={historyColors.price}
                  strokeWidth='2'
                />
              ) : null}
              {showVp ? (
                <polyline
                  points={chartData.allDates
                    .map((item) => {
                      const value = chartData.vpCarryMap.get(item);
                      if (value === null || value === undefined) return null;
                      const x = mapTsToX(toTs(item), chartData.startTs, chartData.endTs);
                      const y = mapValueToY(value, chartData.min, chartData.max);
                      return `${x},${y}`;
                    })
                    .filter((item): item is string => item !== null)
                    .join(' ')}
                  fill='none'
                  stroke={historyColors.vp}
                  strokeWidth='2'
                />
              ) : null}
              {showDy && filtered.dy.length > 0 ? (
                <polyline
                  points={chartData.allDates
                    .map((item) => {
                      const value = chartData.dyCarryMap.get(item);
                      if (value === null || value === undefined) return null;
                      const x = mapTsToX(toTs(item), chartData.startTs, chartData.endTs);
                      const y = mapValueToY(value, chartData.dyMin, chartData.dyMax);
                      return `${x},${y}`;
                    })
                    .filter((item): item is string => item !== null)
                    .join(' ')}
                  fill='none'
                  stroke={historyColors.dy}
                  strokeWidth='2'
                  strokeDasharray='4 3'
                />
              ) : null}
              {showPvp && filtered.pvp.length > 0 ? (
                <polyline
                  points={filtered.pvp
                    .map((item) => {
                      const x = mapTsToX(toTs(item.date), chartData.startTs, chartData.endTs);
                      const y = mapValueToY(item.value, chartData.pvpMin, chartData.pvpMax);
                      return `${x},${y}`;
                    })
                    .join(' ')}
                  fill='none'
                  stroke={historyColors.pvp}
                  strokeWidth='2'
                  strokeDasharray='2 2'
                />
              ) : null}
              {showBenchmark && filtered.benchmark.length > 0 ? (
                <polyline
                  points={chartData.allDates
                    .map((item) => {
                      const value = chartData.normalizedBenchmarkCarryMap.get(item);
                      if (value === null || value === undefined) return null;
                      const x = mapTsToX(toTs(item), chartData.startTs, chartData.endTs);
                      const y = mapValueToY(value, chartData.min, chartData.max);
                      return `${x},${y}`;
                    })
                    .filter((item): item is string => item !== null)
                    .join(' ')}
                  fill='none'
                  stroke={historyColors.benchmark}
                  strokeWidth='2'
                  strokeDasharray='5 4'
                />
              ) : null}
              {filteredCorporateEvents.map((event, index) => {
                const x = mapTsToX(toTs(event.date), chartData.startTs, chartData.endTs);
                const color = getCorporateEventColor(event);
                return (
                  <g key={`${event.date}-${event.eventType}-${index}`}>
                    <line
                      x1={x}
                      y1={chartTop}
                      x2={x}
                      y2={chartBottom}
                      stroke={color}
                      strokeWidth='1.15'
                      strokeDasharray='3 3'
                      opacity='0.72'
                    />
                    <rect x={x - 2} y={chartTop + 2} width='4' height='8' rx='1' fill={color}>
                      <title>
                        {`${getCorporateEventDisplayLabel(event)} em ${DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))}`}
                      </title>
                    </rect>
                  </g>
                );
              })}

              {hoveredDateIndex !== null
                ? (() => {
                    const date = chartData.allDates[hoveredDateIndex];
                    const ts = toTs(date);
                    const x = mapTsToX(ts, chartData.startTs, chartData.endTs);
                    const priceValue = chartData.priceMap.get(date) ?? null;
                    const vpValue = chartData.vpCarryMap.get(date) ?? null;
                    const dyValue = chartData.dyCarryMap.get(date) ?? null;
                    const pvpValue = chartData.pvpMap.get(date) ?? null;
                    const benchmarkValue = chartData.benchmarkCarryMap.get(date) ?? null;
                    return (
                      <>
                        <line
                          x1={x}
                          y1={chartTop}
                          x2={x}
                          y2={chartBottom}
                          stroke='hsl(var(--muted-foreground) / 0.6)'
                          strokeDasharray='4 4'
                        />
                        <g
                          transform={`translate(${Math.min(chartRight - 190, Math.max(chartLeft + 8, x + 10))}, ${chartTop + 8})`}
                        >
                          <rect
                            width='190'
                            height={hasBenchmark && showBenchmark ? '110' : '94'}
                            rx='8'
                            fill='hsl(var(--card))'
                            stroke='hsl(var(--border))'
                          />
                          <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                            {DATE_FORMATTER.format(new Date(`${date}T00:00:00Z`))}
                          </text>
                          <text x='10' y='35' fontSize='12' fill={historyColors.price} opacity={showPrice ? 1 : 0.45}>
                            {`Cotação: ${formatCurrency(priceValue)}`}
                          </text>
                          <text x='10' y='52' fontSize='12' fill={historyColors.vp} opacity={showVp ? 1 : 0.45}>
                            {`VP/cota: ${formatCurrency(vpValue)}`}
                          </text>
                          <text x='10' y='68' fontSize='12' fill={historyColors.dy} opacity={showDy ? 1 : 0.45}>
                            {`DY mês: ${dyValue === null ? '-' : `${PERCENT_FORMATTER.format(dyValue)}%`}`}
                          </text>
                          <text x='10' y='84' fontSize='12' fill={historyColors.pvp} opacity={showPvp ? 1 : 0.45}>
                            {`P/VP: ${pvpValue === null ? '-' : RATIO_FORMATTER.format(pvpValue)}`}
                          </text>
                          {hasBenchmark ? (
                            <text
                              x='10'
                              y='100'
                              fontSize='12'
                              fill={historyColors.benchmark}
                              opacity={showBenchmark ? 1 : 0.45}
                            >
                              {`IFIX: ${benchmarkValue === null ? '-' : NUMBER_FORMATTER.format(benchmarkValue)}`}
                            </text>
                          ) : null}
                        </g>
                      </>
                    );
                  })()
                : null}

              {showDy ? (
                <>
                  <text x={chartRight + 8} y={chartTop + 4} textAnchor='start' fontSize='12' fill={historyColors.dy}>
                    {`${PERCENT_FORMATTER.format(chartData.dyMax)}%`}
                  </text>
                  <text x={chartRight + 8} y={chartBottom + 4} textAnchor='start' fontSize='12' fill={historyColors.dy}>
                    {`${PERCENT_FORMATTER.format(chartData.dyMin)}%`}
                  </text>
                </>
              ) : null}
              {showPvp ? (
                <>
                  <text x={chartRight + 8} y={chartTop + 22} textAnchor='start' fontSize='12' fill={historyColors.pvp}>
                    {`P/VP máx: ${RATIO_FORMATTER.format(chartData.pvpMax)}`}
                  </text>
                  <text x={chartRight + 8} y={chartTop + 38} textAnchor='start' fontSize='12' fill={historyColors.pvp}>
                    {`P/VP mín: ${RATIO_FORMATTER.format(chartData.pvpMin)}`}
                  </text>
                </>
              ) : null}

              <text x={chartLeft} y={height - 16} fontSize='12' fill='hsl(var(--muted-foreground))'>
                {DATE_FORMATTER.format(new Date(`${chartData.allDates[0]}T00:00:00Z`))}
              </text>
              <text x={chartRight} y={height - 16} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {DATE_FORMATTER.format(new Date(`${chartData.allDates[chartData.allDates.length - 1]}T00:00:00Z`))}
              </text>
            </svg>
          </Box>
        )}
        {filteredCorporateEvents.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
              Eventos corporativos no período
            </Typography>
            <Stack direction='row' spacing={0.75} useFlexGap flexWrap='wrap' sx={{ mt: 0.5 }}>
              {filteredCorporateEvents.slice(0, 8).map((event, index) => (
                <Stack
                  key={`${event.date}-${event.eventType}-${index}`}
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{
                    px: 0.75,
                    py: 0.35,
                    borderRadius: 1,
                    backgroundColor: 'hsl(var(--muted) / 0.35)',
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getCorporateEventColor(event),
                    }}
                  />
                  <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    {DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))} ·{' '}
                    {getCorporateEventDisplayLabel(event)}
                  </Typography>
                </Stack>
              ))}
              {filteredCorporateEvents.length > 8 ? (
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  +{filteredCorporateEvents.length - 8} eventos
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Paper>

      {quickSimulationSnapshots.length > 0 ? (
        <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
          >
            <Typography variant='h6' fontWeight={700}>
              Simulação rápida
            </Typography>
            <Button
              component={Link}
              href='simulacao'
              size='small'
              variant='outlined'
              sx={{
                color: accentOutline.text,
                borderColor: accentOutline.border,
                '&:hover': {
                  color: accentOutline.hoverText,
                  borderColor: accentOutline.hoverBorder,
                  backgroundColor: accentOutline.hoverBg,
                },
              }}
            >
              Ver simulação completa
            </Button>
          </Stack>
          <Box
            sx={{
              mt: 1.2,
              display: 'grid',
              gap: 1,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {quickSimulationSnapshots.map((snapshot) => (
              <Box
                key={snapshot.id}
                sx={{
                  p: 1.25,
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 1.5,
                  backgroundColor: 'hsl(var(--muted) / 0.2)',
                }}
              >
                <Typography variant='subtitle2' fontWeight={700}>
                  {snapshot.label}
                </Typography>
                <Typography variant='body2' sx={{ mt: 0.4 }}>
                  Tendo investido {BRL_FORMATTER.format(snapshot.initialAmount)} em{' '}
                  {DATE_FORMATTER.format(new Date(`${snapshot.actualStartDate}T00:00:00Z`))}, na data final{' '}
                  {DATE_FORMATTER.format(new Date(`${snapshot.actualEndDate}T00:00:00Z`))} o valor seria:{' '}
                  <strong>{BRL_FORMATTER.format(snapshot.finalReinvested)}</strong>.
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ mt: 0.55, display: 'block' }}>
                  Sem reinvestimento: {BRL_FORMATTER.format(snapshot.finalPriceOnly)} (
                  {snapshot.returnPriceOnlyPct >= 0 ? '+' : ''}
                  {PERCENT_FORMATTER.format(snapshot.returnPriceOnlyPct)}%)
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  Com reinvestimento: {BRL_FORMATTER.format(snapshot.finalReinvested)} (
                  {snapshot.returnReinvestedPct >= 0 ? '+' : ''}
                  {PERCENT_FORMATTER.format(snapshot.returnReinvestedPct)}%)
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      ) : null}

      <RiskMomentCard
        title='Risco e Momento (período selecionado)'
        tickerLabel={fundProfile.ticker}
        priceSeries={filtered.price.map((item) => ({
          date: item.date,
          close: item.value,
        }))}
        benchmarkSeries={filtered.benchmark.map((item) => ({
          date: item.date,
          close: item.value,
        }))}
      />

      {portfolioHighlights ? <PortfolioHighlightsPanel highlights={portfolioHighlights} /> : null}

      {shouldRenderReportActions(g2rsMode) ? (
        <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
            sx={{ mb: 1.25 }}
          >
            <Typography variant='h6' fontWeight={700}>
              Últimos relatórios gerenciais
            </Typography>
            <Button
              component={Link}
              href={reportPathForTicker(tickerLabel)}
              variant='outlined'
              size='small'
              sx={{
                color: accentOutline.text,
                borderColor: accentOutline.border,
                '&:hover': {
                  color: accentOutline.hoverText,
                  borderColor: accentOutline.hoverBorder,
                  backgroundColor: accentOutline.hoverBg,
                },
              }}
            >
              Ver todos os relatórios
            </Button>
          </Stack>
          {latestReportSummaries.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Ainda não há resumos de relatórios para este fundo.
            </Typography>
          ) : (
            <LatestReportsGrid latestReportSummaries={latestReportSummaries} />
          )}
        </Paper>
      ) : null}
    </Stack>
  );
}

const LatestReportsGrid = memo(function LatestReportsGrid({
  latestReportSummaries,
}: {
  latestReportSummaries: TickerSummaryTabClientProps['latestReportSummaries'];
}) {
  const rows = useMemo(
    () =>
      latestReportSummaries.map((item) => ({
        id: item.id,
        dataEntrega: item.deliveryDate
          ? DATE_FORMATTER.format(new Date(`${item.deliveryDate}T00:00:00Z`))
          : 'Data não informada',
        titulo: item.title,
        sentimentoGeral: item.sentimentoGeral ?? '-',
        sentimentoDy: item.sentimentoDy ?? '-',
        sentimentoVacanciaInadimplencia: item.sentimentoVacanciaInadimplencia ?? '-',
        resumo: item.resumo ?? 'Resumo indisponível para este relatório.',
      })),
    [latestReportSummaries],
  );

  return (
    <GridComponent
      dataSource={rows}
      allowPaging={false}
      allowSorting
      allowResizing
      width='100%'
      allowTextWrap
      textWrapSettings={{ wrapMode: 'Content' }}
      resizeSettings={{ mode: 'Auto' }}
      gridLines='Horizontal'
      height='auto'
    >
      <ColumnsDirective>
        <ColumnDirective field='dataEntrega' headerText='Data' width='9%' minWidth='70' />
        <ColumnDirective field='titulo' headerText='Relatório' width='23%' minWidth='170' />
        <ColumnDirective
          field='resumo'
          headerText='Resumo'
          width='44%'
          minWidth='260'
          template={(props: { resumo: string }) => (
            <div
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                lineHeight: 1.35,
                padding: '6px 0',
              }}
            >
              {props.resumo}
            </div>
          )}
        />
        <ColumnDirective field='sentimentoGeral' headerText='Sent. geral' width='8%' minWidth='80' />
        <ColumnDirective field='sentimentoDy' headerText='Sent. DY' width='7%' minWidth='70' />
        <ColumnDirective
          field='sentimentoVacanciaInadimplencia'
          headerText='Sent. vac./inad.'
          width='10%'
          minWidth='110'
        />
      </ColumnsDirective>
      <Inject services={[Sort, Resize, Page]} />
    </GridComponent>
  );
});

type PortfolioHighlights = NonNullable<TickerSummaryTabClientProps['portfolioHighlights']>;

function PortfolioHighlightsPanel({ highlights }: { highlights: PortfolioHighlights }) {
  const colors = useAppColors();
  const accentOutline = colors.actions.accentOutline;
  const topPositionsMax = Math.max(
    1,
    ...highlights.topPositions.map((item) => item.portfolioPercent ?? 0).filter((value) => Number.isFinite(value)),
  );
  const mixItems = highlights.mix
    ? [
        {
          label: 'Imóveis',
          value: highlights.mix.realEstatePercent,
          color: colors.charts.portfolioMix.realEstate,
        },
        {
          label: 'Fundos',
          value: highlights.mix.fundsPercent,
          color: colors.charts.portfolioMix.funds,
        },
        {
          label: 'Ativos financeiros',
          value: highlights.mix.financialAssetsPercent,
          color: colors.charts.portfolioMix.financialAssets,
        },
        {
          label: 'Participações',
          value: highlights.mix.equitiesPercent,
          color: colors.charts.portfolioMix.equities,
        },
      ].sort((a, b) => b.value - a.value)
    : [];
  const riskIpcaIgpm = highlights.riskExposure?.ipcaIgpmPercent ?? null;
  const riskMaturity12m = highlights.riskExposure?.maturityUpTo12MonthsPercent ?? null;
  const hasAnyRiskExposure = riskIpcaIgpm !== null || riskMaturity12m !== null;
  const financialPortfolioPercent = highlights.financialPortfolioPercent;
  const ipcaIgpmWithinFinancial =
    riskIpcaIgpm !== null && financialPortfolioPercent !== null && financialPortfolioPercent > 0
      ? (riskIpcaIgpm / financialPortfolioPercent) * 100
      : null;
  const maturity12mWithinFinancial =
    riskMaturity12m !== null && financialPortfolioPercent !== null && financialPortfolioPercent > 0
      ? (riskMaturity12m / financialPortfolioPercent) * 100
      : null;
  const concentrationTijolo =
    highlights.concentrationByProfile?.realEstateTop1Percent ?? highlights.concentration?.top1Percent ?? null;
  const concentrationPapel =
    highlights.concentrationByProfile?.paperTop1Percent ?? highlights.concentration?.top1Percent ?? null;
  const concentrationTijoloScore = Math.max(0, Math.min(100, concentrationTijolo ?? 0));
  const concentrationPapelScore = Math.max(0, Math.min(100, concentrationPapel ?? 0));
  const topRealEstateStates = highlights.realEstateTopStates;
  const topRealEstateStatesBasis = topRealEstateStates.length > 0 ? topRealEstateStates[0].basis : null;
  const concentrationBand = (value: number | null) =>
    value === null
      ? 'Não informado'
      : value >= 40
        ? 'Muito concentrado'
        : value >= 20
          ? 'Concentração moderada'
          : 'Baixa concentração';

  return (
    <Paper
      variant='outlined'
      sx={{
        p: { xs: 1.5, md: 2 },
        background: 'linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--muted) / 0.18) 100%)',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Typography variant='h6' fontWeight={700}>
          Carteira e Destaques
        </Typography>
        <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
          <Chip
            size='small'
            label={`${highlights.coverageLabel}${highlights.referenceDate ? ` • ${DATE_FORMATTER.format(new Date(`${highlights.referenceDate}T00:00:00Z`))}` : ''}`}
            variant='outlined'
          />
          <Button
            component={Link}
            href='carteira'
            size='small'
            variant='outlined'
            sx={{
              color: accentOutline.text,
              borderColor: accentOutline.border,
              '&:hover': {
                color: accentOutline.hoverText,
                borderColor: accentOutline.hoverBorder,
                backgroundColor: accentOutline.hoverBg,
              },
            }}
          >
            Ver carteira completa
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          mt: 1.25,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            p: 1.25,
            border: '1px solid hsl(var(--border))',
            borderRadius: 1.5,
            borderTop: `3px solid ${colors.highlights.topBorder.primary}`,
          }}
        >
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Typography variant='subtitle2' fontWeight={700}>
              Top 5 posições (% PL)
            </Typography>
            <InfoDotTooltip text='Mostra as cinco maiores posições investidas pelo fundo como % do portfólio no período de referência. Quando a CVM não informa pesos por posição, usamos peso uniforme (1) entre as posições disponíveis para estimar a participação relativa.' />
          </Stack>
          <Stack spacing={0.75} sx={{ mt: 0.8 }}>
            {highlights.topPositions.length > 0 ? (
              highlights.topPositions.map((item) => {
                const value = item.portfolioPercent ?? 0;
                return (
                  <Stack key={item.name} spacing={0.45}>
                    <Stack direction='row' justifyContent='space-between'>
                      <Typography variant='body2'>{item.name}</Typography>
                      <Typography variant='body2' fontWeight={700}>
                        {item.portfolioPercent === null ? '-' : `${PERCENT_FORMATTER.format(item.portfolioPercent)}%`}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant='determinate'
                      value={Math.max(0, Math.min(100, (value / topPositionsMax) * 100))}
                      sx={{
                        height: 7,
                        borderRadius: 999,
                        backgroundColor: 'hsl(var(--muted))',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.highlights.progressBar.primary,
                        },
                      }}
                    />
                  </Stack>
                );
              })
            ) : (
              <Typography variant='body2' color='text.secondary'>
                Sem posições detalhadas no período.
              </Typography>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            p: 1.25,
            border: '1px solid hsl(var(--border))',
            borderRadius: 1.5,
            borderTop: `3px solid ${colors.highlights.topBorder.success}`,
          }}
        >
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Typography variant='subtitle2' fontWeight={700}>
              Mix da carteira
            </Typography>
            <InfoDotTooltip text='Distribuição do patrimônio investido por classe de ativo (imóveis, fundos, ativos financeiros e participações), com base nos últimos informes da CVM.' />
          </Stack>
          {mixItems.length > 0 ? (
            <Stack spacing={0.8} sx={{ mt: 0.8 }}>
              {mixItems.map((item) => (
                <Stack key={item.label} spacing={0.45}>
                  <Stack direction='row' justifyContent='space-between'>
                    <Stack direction='row' spacing={0.6} alignItems='center'>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          bgcolor: item.color,
                        }}
                      />
                      <Typography variant='body2'>{item.label}</Typography>
                    </Stack>
                    <Typography variant='body2' fontWeight={700}>
                      {PERCENT_FORMATTER.format(item.value)}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant='determinate'
                    value={Math.max(0, Math.min(100, item.value))}
                    sx={{
                      height: 7,
                      borderRadius: 999,
                      backgroundColor: 'hsl(var(--muted))',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: item.color,
                      },
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.8 }}>
              Sem dados de composição no período.
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            p: 1.25,
            border: '1px solid hsl(var(--border))',
            borderRadius: 1.5,
            borderTop: `3px solid ${colors.highlights.topBorder.warning}`,
          }}
        >
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Typography variant='subtitle2' fontWeight={700}>
              Concentração (Tijolo e Papel)
            </Typography>
            <InfoDotTooltip text='Método: índice simples = maior posição / total do próprio perfil. Tijolo usa a base de imóveis; Papel usa a base de posições de papel. Quando a CVM não informa pesos por posição, usamos peso uniforme (1) entre as posições disponíveis para estimar o cálculo. 100% indica uma única posição no perfil.' />
          </Stack>
          <Stack spacing={0.75} sx={{ mt: 0.8 }}>
            <Typography variant='body2'>
              <strong>Tijolo:</strong>{' '}
              {concentrationTijolo === null
                ? 'Não informado'
                : `${PERCENT_FORMATTER.format(concentrationTijolo)}% (${concentrationBand(concentrationTijolo)})`}
              {highlights.mix?.realEstatePercent !== null && highlights.mix?.realEstatePercent !== undefined
                ? ` • ${PERCENT_FORMATTER.format(highlights.mix.realEstatePercent)}% da carteira`
                : ''}
            </Typography>
            <LinearProgress
              variant='determinate'
              value={concentrationTijoloScore}
              sx={{
                height: 8,
                borderRadius: 999,
                backgroundColor: 'hsl(var(--muted))',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colors.highlights.progressBar.primary,
                },
              }}
            />
            <Stack spacing={0.4}>
              <Typography variant='caption' color='text.secondary'>
                Distribuição por UF (imóveis)
                {topRealEstateStatesBasis === null
                  ? ''
                  : topRealEstateStatesBasis === 'count'
                    ? ' • por quantidade (Top 5)'
                    : ' • por valor (Top 5)'}
              </Typography>
              <Stack direction='row' spacing={0.6} useFlexGap flexWrap='wrap'>
                {topRealEstateStates.length > 0 ? (
                  topRealEstateStates.map((item) => (
                    <Chip
                      key={`uf-${item.state}`}
                      size='small'
                      variant='outlined'
                      label={`${item.state} ${PERCENT_FORMATTER.format(item.percent)}%`}
                    />
                  ))
                ) : (
                  <Typography variant='caption' color='text.secondary'>
                    Não informado
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Typography variant='body2'>
              <strong>Papel:</strong>{' '}
              {concentrationPapel === null
                ? 'Não informado'
                : `${PERCENT_FORMATTER.format(concentrationPapel)}% (${concentrationBand(concentrationPapel)})`}
              {financialPortfolioPercent !== null
                ? ` • ${PERCENT_FORMATTER.format(financialPortfolioPercent)}% da carteira`
                : ''}
            </Typography>
            <LinearProgress
              variant='determinate'
              value={concentrationPapelScore}
              sx={{
                height: 8,
                borderRadius: 999,
                backgroundColor: 'hsl(var(--muted))',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: colors.highlights.progressBar.warning,
                },
              }}
            />
          </Stack>
        </Box>

        <Box
          sx={{
            p: 1.25,
            border: '1px solid hsl(var(--border))',
            borderRadius: 1.5,
            borderTop: `3px solid ${colors.highlights.topBorder.purple}`,
          }}
        >
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Typography variant='subtitle2' fontWeight={700}>
              Carteira financeira (Fundos + Ativos financeiros, sobre patrimônio)
            </Typography>
            <InfoDotTooltip text='Parcela da carteira financeira (fundos + ativos financeiros) em relação ao patrimônio total do fundo, com leitura de indexadores e vencimento curto quando disponível.' />
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 0.8 }}>
            <Typography variant='body2'>
              Carteira financeira: <strong>{formatPercentOrNotReported(financialPortfolioPercent)}</strong>
            </Typography>
            <Typography variant='body2'>
              Participações societárias (fora da carteira financeira):{' '}
              <strong>{formatPercentOrNotReported(highlights.mix?.equitiesPercent ?? null)}</strong>
            </Typography>
            <Typography variant='body2'>
              Exposição IPCA + IGPM:{' '}
              <strong>
                {riskIpcaIgpm === null
                  ? 'Não informado'
                  : `${PERCENT_FORMATTER.format(riskIpcaIgpm)}% do PL${
                      ipcaIgpmWithinFinancial !== null
                        ? ` (${PERCENT_FORMATTER.format(ipcaIgpmWithinFinancial)}% da carteira financeira)`
                        : ''
                    }`}
              </strong>
            </Typography>
            <Typography variant='body2'>
              Vencimento até 12m (da carteira financeira):{' '}
              <strong>
                {riskMaturity12m === null
                  ? 'Não informado'
                  : `${PERCENT_FORMATTER.format(riskMaturity12m)}% do PL${
                      maturity12mWithinFinancial !== null
                        ? ` (${PERCENT_FORMATTER.format(maturity12mWithinFinancial)}% da carteira financeira)`
                        : ''
                    }`}
              </strong>
            </Typography>
          </Stack>
          {!hasAnyRiskExposure ? (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.8 }}>
              CVM não informou esses campos no informe trimestral complementar mais recente
              {highlights.referenceDate
                ? ` (${DATE_FORMATTER.format(new Date(`${highlights.referenceDate}T00:00:00Z`))}).`
                : '.'}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <PortfolioExecutiveSummariesPanel highlights={highlights} />
    </Paper>
  );
}

function PortfolioExecutiveSummariesPanel({ highlights }: { highlights: PortfolioHighlights }) {
  const colors = useAppColors();
  const cash = highlights.cashSummary;
  const tijolo = highlights.realEstateSummary;
  const papel = highlights.paperSummary;
  const mix = highlights.mix;
  const tijoloWeightLabel = mix === null ? null : `${PERCENT_FORMATTER.format(mix.realEstatePercent)}% da carteira`;
  const papelWeight = mix === null ? null : mix.financialAssetsPercent + mix.fundsPercent;
  const papelWeightLabel = papelWeight === null ? null : `${PERCENT_FORMATTER.format(papelWeight)}% da carteira`;

  const showCash =
    cash !== null &&
    (cash.cashTotal !== null ||
      cash.receivableTotal !== null ||
      cash.payableTotal !== null ||
      cash.netLiquidity !== null ||
      cash.coverageRatio !== null ||
      cash.cashOverInvestedPercent !== null ||
      cash.netLiquidityDelta !== null);
  const showTijolo =
    tijolo !== null &&
    (tijolo.totalAssets !== null ||
      tijolo.vacancyAvgPercent !== null ||
      tijolo.delinquencyAvgPercent !== null ||
      tijolo.typicalContractsPercent !== null ||
      tijolo.atypicalContractsPercent !== null ||
      tijolo.assetsWithVacancy !== null ||
      tijolo.assetsWithDelinquency !== null);
  const showPapel =
    papel !== null &&
    (papel.financialAssetsPercent !== null ||
      papel.fundsPercent !== null ||
      papel.top5ConcentrationPercent !== null ||
      papel.inflationExposurePercent !== null ||
      papel.maturityUpTo12MonthsPercent !== null ||
      papel.dominantIndexerPercent !== null);

  if (!showCash && !showTijolo && !showPapel) return null;

  return (
    <Box
      sx={{
        mt: 1.25,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        gap: 1.25,
      }}
    >
      {showCash && cash ? (
        <ExecutiveSummaryBlock
          title='Resumo de Caixa'
          detailsHref='caixa'
          tone={colors.highlights.topBorder.primary}
          chips={[
            `Caixa: ${formatCompactCurrency(cash.cashTotal)}`,
            `A receber: ${formatCompactCurrency(cash.receivableTotal)}`,
            `A pagar: ${formatCompactCurrency(cash.payableTotal)}`,
            `Folga líquida: ${formatCompactCurrency(cash.netLiquidity)}`,
          ]}
          summary={buildCashExecutiveSummary(cash)}
        />
      ) : null}

      {showTijolo && tijolo ? (
        <ExecutiveSummaryBlock
          title={tijoloWeightLabel ? `Resumo para Tijolo (${tijoloWeightLabel})` : 'Resumo para Tijolo'}
          detailsHref='carteira'
          tone={colors.highlights.topBorder.success}
          chips={[
            `Vacância média: ${formatPercentOrNotReported(tijolo.vacancyAvgPercent)}`,
            `Inadimplência média: ${formatPercentOrNotReported(tijolo.delinquencyAvgPercent)}`,
            `Típicos: ${formatPercentOrNotReported(tijolo.typicalContractsPercent)}`,
            `Atípicos: ${formatPercentOrNotReported(tijolo.atypicalContractsPercent)}`,
          ]}
          summary={buildRealEstateExecutiveSummary(tijolo)}
        />
      ) : null}

      {showPapel && papel ? (
        <ExecutiveSummaryBlock
          title={papelWeightLabel ? `Resumo para Papel (${papelWeightLabel})` : 'Resumo para Papel'}
          detailsHref='carteira'
          tone={colors.highlights.topBorder.warning}
          chips={[
            `Ativos financeiros: ${formatPercentOrNotReported(papel.financialAssetsPercent)}`,
            `Fundos na carteira: ${formatPercentOrNotReported(papel.fundsPercent)}`,
            `Top 5 ativos: ${formatPercentOrNotReported(papel.top5ConcentrationPercent)}`,
            `Inflação (IPCA+IGPM): ${formatPercentOrNotReported(papel.inflationExposurePercent)}`,
          ]}
          summary={buildPaperExecutiveSummary(papel)}
        />
      ) : null}
    </Box>
  );
}

function ExecutiveSummaryBlock({
  title,
  detailsHref,
  tone,
  chips,
  summary,
}: {
  title: string;
  detailsHref: string;
  tone: string;
  chips: string[];
  summary: string;
}) {
  const colors = useAppColors();
  const accentOutline = colors.actions.accentOutline;
  return (
    <Box
      sx={{
        p: 1.25,
        border: '1px solid hsl(var(--border))',
        borderRadius: 1.5,
        borderTop: `3px solid ${tone}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={0.8}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Typography variant='subtitle2' fontWeight={800}>
          {title}
        </Typography>
        <Button
          component={Link}
          href={detailsHref}
          size='small'
          variant='outlined'
          sx={{
            color: accentOutline.text,
            borderColor: accentOutline.border,
            '&:hover': {
              color: accentOutline.hoverText,
              borderColor: accentOutline.hoverBorder,
              backgroundColor: accentOutline.hoverBg,
            },
          }}
        >
          Ver detalhes
        </Button>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.55 }}>
        {summary}
      </Typography>
      <Stack direction='row' spacing={0.7} useFlexGap flexWrap='wrap' sx={{ mt: 1 }}>
        {chips.map((chip) => (
          <Chip key={`${title}-${chip}`} size='small' variant='outlined' label={chip} />
        ))}
      </Stack>
    </Box>
  );
}

function buildCashExecutiveSummary(cash: NonNullable<PortfolioHighlights['cashSummary']>) {
  const coverage =
    cash.coverageRatio === null ? 'sem métrica de cobertura' : `${RATIO_FORMATTER.format(cash.coverageRatio)}x`;
  const delta =
    cash.netLiquidityDelta === null
      ? 'sem comparação com o período anterior'
      : cash.netLiquidityDelta >= 0
        ? `folga líquida em alta (${formatCompactCurrency(cash.netLiquidityDelta)})`
        : `folga líquida em queda (${formatCompactCurrency(cash.netLiquidityDelta)})`;
  return `Cobertura de obrigações em ${coverage}, com ${delta}.`;
}

function buildRealEstateExecutiveSummary(summary: NonNullable<PortfolioHighlights['realEstateSummary']>) {
  const totalCountLabel =
    summary.totalAssets === null ? 'total não informado' : NUMBER_FORMATTER.format(summary.totalAssets);
  const totalUnitLabel = summary.totalAssets !== null && summary.totalAssets === 1 ? 'imóvel' : 'imóveis';
  const vacancyLoad =
    summary.assetsWithVacancy === null
      ? 'vacância sem detalhamento por imóvel'
      : `${NUMBER_FORMATTER.format(summary.assetsWithVacancy)} de ${totalCountLabel} ${totalUnitLabel} com vacância`;
  const delinquencyLoad =
    summary.assetsWithDelinquency === null
      ? 'inadimplência sem detalhamento por imóvel'
      : `${NUMBER_FORMATTER.format(summary.assetsWithDelinquency)} de ${totalCountLabel} ${totalUnitLabel} com inadimplência`;
  const contractMix =
    summary.typicalContractsPercent !== null || summary.atypicalContractsPercent !== null
      ? `mix contratual em ${formatPercentOrNotReported(summary.typicalContractsPercent)} típicos e ${formatPercentOrNotReported(summary.atypicalContractsPercent)} atípicos`
      : 'mix contratual sem leitura disponível';
  return `${vacancyLoad}, ${delinquencyLoad}, ${contractMix}.`;
}

function buildPaperExecutiveSummary(summary: NonNullable<PortfolioHighlights['paperSummary']>) {
  const indexer =
    summary.dominantIndexerLabel && summary.dominantIndexerPercent !== null
      ? `${summary.dominantIndexerLabel} em ${PERCENT_FORMATTER.format(summary.dominantIndexerPercent)}%`
      : 'indexador dominante não informado';
  const duration =
    summary.maturityUpTo12MonthsPercent === null
      ? 'sem leitura de vencimento curto'
      : `${PERCENT_FORMATTER.format(summary.maturityUpTo12MonthsPercent)}% com vencimento até 12 meses`;
  return `Carteira com foco em crédito e fundos, ${indexer}, e ${duration}.`;
}

const formatCurrency = (value: number | null) => (value === null ? '-' : BRL_FORMATTER.format(value));
const formatCompactCurrency = (value: number | null) => {
  if (value === null) return '-';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000_000)} bi`;
  if (abs >= 1_000_000) return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000)} mi`;
  if (abs >= 1_000) return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000)} mil`;
  return BRL_FORMATTER.format(value);
};

const formatNumber = (value: number | null) => (value === null ? '-' : NUMBER_FORMATTER.format(value));

const formatPercentOrNotReported = (value: number | null | undefined) =>
  value === null || value === undefined ? 'Não informado' : `${PERCENT_FORMATTER.format(value)}%`;

function InfoDotTooltip({ text }: { text: string }) {
  return (
    <Tooltip title={text} arrow placement='top'>
      <Box
        component='span'
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: '1px solid hsl(var(--border))',
          color: 'text.secondary',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1,
          cursor: 'help',
          userSelect: 'none',
        }}
        aria-label='Entenda o indicador'
      >
        i
      </Box>
    </Tooltip>
  );
}

function SeriesToggleButton({
  label,
  color,
  active,
  onClick,
}: {
  label: string;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size='small'
      variant='outlined'
      onClick={onClick}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        gap: 0.75,
        borderColor: active ? color : 'hsl(var(--border))',
        bgcolor: active ? 'hsl(var(--muted) / 0.4)' : 'transparent',
        color: active ? color : 'text.secondary',
        '&:hover': {
          borderColor: color,
          bgcolor: active ? 'hsl(var(--muted) / 0.55)' : 'hsl(var(--muted) / 0.22)',
        },
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: color,
        }}
      />
      {label}
    </Button>
  );
}
